import {AfterViewChecked, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {ActivatedRoute, Params, Route, Router} from '@angular/router';
import {LanguageService} from './services/language/language.service';
import {LoginService} from './services/login/login.service';
import {mergeMap, takeUntil} from 'rxjs/operators';
import {BaseActions} from './shared/base-actions';
import {ErrorMessageConstants} from './shared/error-message-constants';
import {MaintenanceService} from './services/maintenance/maintenance-service';
import {MaintenanceModeConfig} from './model/maintenance-config/maintenance-mode-config';
import {RoutingUtils} from './shared/utils/router/routing-utils';
import {CarouselService} from './services/carousel/carousel.service';
import {OcapiService} from './services/ocapi/ocapi.service';
import {TvShopStateService} from './services/state/tv-shop-state.service';
import {
  TvShopEntertainmentPackGroupIds
} from './constants/tv-shop-entertainment-pack/tv-shop-entertainment-pack-group.constants';
import {
  NetflixServicesDetailsComponent
} from './detail-pages/netflix-services-details/netflix-services-details.component';
import {OmapiIdsUtils} from './shared/utils/omapi-ids/omapi-ids-utils';
import {
  StreamingBundleOptionsComponent
} from './detail-pages/netflix-services-details/streaming-bundle-options/streaming-bundle-options.component';
import {
  StandaloneFlavorsComponent
} from './detail-pages/netflix-services-details/standalone-flavors/standalone-flavors.component';
import {OmapiIdConstants} from './shared/omapi-id-constants';
import {PlayDetailsComponent} from './detail-pages/play-details/play-details.component';
import {PlayMoreDetailsComponent} from './detail-pages/play-more-details/play-more-details.component';
import {TvshopComponent} from './tv-shop-component/tv-shop-component';
import {DeepLinkingComponent} from './deep-linking/deep-linking.component';
import {ChangeLogComponent} from './change-log/change-log.component';
import {PlaySportsDetailsComponent} from './detail-pages/play-sports-details/play-sports-details.component';
import {RtlSeriesDetailsComponent} from './detail-pages/rtl-series-details/rtl-series-details.component';
import {OptionFrDetailsComponent} from './detail-pages/option-fr/option-fr-details.component';
import {BeTvDetailsComponent} from './detail-pages/be-tv/be-tv-details.component';
import {BeTvSportsDetailsComponent} from './detail-pages/be-tv-sports/be-tv-sports-details.component';
import {PassionXlDetailsComponent} from './detail-pages/passion-xl/passion-xl-details.component';
import {DiscoverMoreDetailsComponent} from './detail-pages/discover-more-details/discover-more-details.component';
import {FamilyFunDetailsComponent} from './detail-pages/family-fun-details/family-fun-details.component';
import {BouquetArabeDetailsComponent} from './detail-pages/bouquet-arabe-details/bouquet-arabe-details.component';
import {
  BouquetAfricainDetailsComponent
} from './detail-pages/bouquet-africain-details/bouquet-africain-details.component';
import {VrtNetGemistDetailsComponent} from './detail-pages/vrt-net-gemist/vrt-net-gemist-details.component';
import {TrialPageComponent} from './detail-pages/trial-page/trial-page.component';
import {RedCarpetMoreDetailsComponent} from './detail-pages/red-carpet-more-details/red-carpet-more-details.component';
import {StreamzBasicDetailsComponent} from './detail-pages/streamz-basic-details/streamz-basic-details.component';
import {DisneyServicesDetailsComponent} from './detail-pages/disney-service-details/disney-services-details.component';
import {PackOptionSelectionComponent} from './shared/pack-option-selection/pack-option-selection.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.less']
})

export class AppComponent extends BaseActions implements OnInit, AfterViewChecked {
  loader$ = this.stateService.loader$;
  hasError$ = this.stateService.hasError$;
  isInitiated$ = this.stateService.isInitiated$;
  maintenanceModeConfig: MaintenanceModeConfig;
  isMaintenanceModeActive: boolean;

  constructor(private languageService: LanguageService,
              private loginService: LoginService,
              private route: ActivatedRoute,
              private router: Router,
              private maintenanceService: MaintenanceService,
              private ocapiService: OcapiService,
              private carouselService: CarouselService,
              private readonly changeDetectorRef: ChangeDetectorRef,
              private stateService: TvShopStateService) {
    super();
  }

  ngOnInit(): void {
    this.languageService.setLanguage();
    this.getCpeIdFromUrl();
    this.checkForPromoStartDate();
    if (!RoutingUtils.getPath().startsWith('/status/')) {
      this.initMaintanceMode();
    }
  }

  ngAfterViewChecked(): void {
    this.changeDetectorRef.detectChanges();
  }

  private initMaintanceMode() {
    this.maintenanceService.getMaintenanceMode().pipe(takeUntil(this.stopSubscription)).subscribe(
      config => {
        this.loadMaintenanceModeProperties(config);
        if (!this.isMaintenanceModeActive) {
          this.initApp();
        } else {
          this.stateService.setLoading(false);
        }
      },
      error => {
        this.initApp();
      }
    );
  }

  private getCpeIdFromUrl(): void {
    setTimeout(() => {
      const queryParam = this.route.snapshot.queryParams['cpeid'];
      const queryParamB = this.route.snapshot.queryParams['CPEid'];
      const cpeid = queryParam ? queryParam : queryParamB ? queryParamB : '';
      this.ocapiService.setCpeidBehaviorSbj(cpeid);
    });
  }

  private initApp() {
    this.stateService.login().pipe(
      mergeMap(() => {
        const PRODUCT_DETAILS = 'product-details/';
        const OPTIONS = '/option';

        const NetflixDetailRoutes: Route[] = [
          {
            path: PRODUCT_DETAILS + TvShopEntertainmentPackGroupIds.NETFLIX_STANDALONE,
            component: NetflixServicesDetailsComponent,
            data: {omapiId: TvShopEntertainmentPackGroupIds.NETFLIX_STANDALONE}
          },
          {
            path: PRODUCT_DETAILS + OmapiIdsUtils.NETFLIX_STANDARD_STREAMZ_ID,
            component: NetflixServicesDetailsComponent,
            data: {omapiId: OmapiIdsUtils.NETFLIX_STANDARD_STREAMZ_ID}
          },
          {
            path: PRODUCT_DETAILS + OmapiIdsUtils.NETFLIX_STANDARD_STREAMZPLUS_ID,
            component: NetflixServicesDetailsComponent,
            data: {omapiId: OmapiIdsUtils.NETFLIX_STANDARD_STREAMZPLUS_ID}
          },
          {
            path: PRODUCT_DETAILS + OmapiIdsUtils.NETFLIX_STANDARD_PLAYMORE_ID,
            component: NetflixServicesDetailsComponent,
            data: {omapiId: OmapiIdsUtils.NETFLIX_STANDARD_PLAYMORE_ID}
          },
          {
            path: PRODUCT_DETAILS + OmapiIdsUtils.NETFLIX_STANDARD_STREAMZ_ID + OPTIONS,
            component: StreamingBundleOptionsComponent,
            data: {omapiId: OmapiIdsUtils.NETFLIX_STANDARD_PLAYMORE_ID}
          },
          {
            path: PRODUCT_DETAILS + OmapiIdsUtils.NETFLIX_STANDARD_STREAMZPLUS_ID + OPTIONS,
            component: StreamingBundleOptionsComponent,
            data: {omapiId: OmapiIdsUtils.NETFLIX_STANDARD_STREAMZPLUS_ID}
          },
          {
            path: PRODUCT_DETAILS + OmapiIdsUtils.NETFLIX_STANDARD_PLAYMORE_ID + OPTIONS,
            component: StreamingBundleOptionsComponent,
            data: {omapiId: OmapiIdsUtils.NETFLIX_STANDARD_PLAYMORE_ID}
          },
          {
            path: PRODUCT_DETAILS + TvShopEntertainmentPackGroupIds.NETFLIX_STANDALONE + OPTIONS,
            component: StandaloneFlavorsComponent,
            data: {omapiId: TvShopEntertainmentPackGroupIds.NETFLIX_STANDALONE}
          },
          {
            path: PRODUCT_DETAILS + OmapiIdsUtils.DISNEY_PLUS_ID,
            component: DisneyServicesDetailsComponent,
            data: {omapiId: OmapiIdsUtils.DISNEY_PLUS_ID}
          },
        ];

        const StreamzRoutes: Route[] = [
          {path: PRODUCT_DETAILS + OmapiIdConstants.STREAMZ, component: PlayDetailsComponent},
          {path: PRODUCT_DETAILS + OmapiIdConstants.PLAY_MORE, component: PlayMoreDetailsComponent},
          {path: PRODUCT_DETAILS + OmapiIdConstants.STREAMZ_BASIC, component: StreamzBasicDetailsComponent},
          {
            path: PRODUCT_DETAILS + OmapiIdConstants.STREAMZ + OPTIONS,
            component: PackOptionSelectionComponent,
            data: {omapiId: OmapiIdConstants.STREAMZ}
          },
          {
            path: PRODUCT_DETAILS + OmapiIdConstants.STREAMZ_BASIC + OPTIONS,
            component: PackOptionSelectionComponent,
            data: {omapiId: OmapiIdConstants.STREAMZ_BASIC}
          },
          {
            path: PRODUCT_DETAILS + OmapiIdsUtils.STREAMZ_BASIC_DWN_BUSINESS_ID + OPTIONS,
            component: PackOptionSelectionComponent,
            data: {omapiId: OmapiIdsUtils.STREAMZ_BASIC_DWN_BUSINESS_ID}
          },
          {
            path: PRODUCT_DETAILS + OmapiIdsUtils.STREAMZ_DWN_BUSINESS_ID + OPTIONS,
            component: PackOptionSelectionComponent,
            data: {omapiId: OmapiIdsUtils.STREAMZ_DWN_BUSINESS_ID}
          }

        ];

        const ProductDetailRoutes: Route[] = [
          {path: 'index.html', component: TvshopComponent, pathMatch: 'full'},
          {path: '', redirectTo: 'index.html', pathMatch: 'full'},
          {path: 'salesoffer:id', component: DeepLinkingComponent},
          {path: 'salesoffer', component: DeepLinkingComponent},
          {path: 'status/change-log', component: ChangeLogComponent},
          {path: PRODUCT_DETAILS + OmapiIdConstants.PLAY_SPORTS, component: PlaySportsDetailsComponent},
          {path: PRODUCT_DETAILS + OmapiIdConstants.RTL_SERIES, component: RtlSeriesDetailsComponent},
          {path: PRODUCT_DETAILS + OmapiIdConstants.OPTION_FR, component: OptionFrDetailsComponent},
          {
            path: PRODUCT_DETAILS + OmapiIdConstants.BE_TV_VLA,
            component: BeTvDetailsComponent,
            data: {omapiId: OmapiIdConstants.BE_TV_VLA}
          },
          {
            path: PRODUCT_DETAILS + OmapiIdConstants.BE_TV_BXL_WAL,
            component: BeTvDetailsComponent,
            data: {omapiId: OmapiIdConstants.BE_TV_BXL_WAL}
          },
          {
            path: PRODUCT_DETAILS + OmapiIdConstants.BE_TV_SPORT_VLA,
            component: BeTvSportsDetailsComponent,
            data: {omapiId: OmapiIdConstants.BE_TV_SPORT_VLA}
          },
          {
            path: PRODUCT_DETAILS + OmapiIdConstants.BE_TV_SPORT_BXL_WAL,
            component: BeTvSportsDetailsComponent,
            data: {omapiId: OmapiIdConstants.BE_TV_SPORT_BXL_WAL}
          },
          {path: PRODUCT_DETAILS + OmapiIdConstants.PASSION_XL, component: PassionXlDetailsComponent},
          {path: PRODUCT_DETAILS + OmapiIdConstants.DISCOVER_MORE, component: DiscoverMoreDetailsComponent},
          {path: PRODUCT_DETAILS + OmapiIdConstants.FAMILY_FUN, component: FamilyFunDetailsComponent},
          {path: PRODUCT_DETAILS + OmapiIdConstants.BOUQUET_ARABE, component: BouquetArabeDetailsComponent},
          {path: PRODUCT_DETAILS + OmapiIdConstants.BOUQUET_AFRICAIN, component: BouquetAfricainDetailsComponent},
          {path: PRODUCT_DETAILS + OmapiIdConstants.VRT_NET_GEMIST, component: VrtNetGemistDetailsComponent},
          {path: PRODUCT_DETAILS + OmapiIdConstants.TRIALS, component: TrialPageComponent},
          {path: PRODUCT_DETAILS + OmapiIdConstants.RED_CARPET_MORE, component: RedCarpetMoreDetailsComponent},
          ...NetflixDetailRoutes,
          ...StreamzRoutes
        ];
        return this.stateService.initialise(ProductDetailRoutes);
      })
    ).subscribe(() => {
      this.navigateToDeeplinkingPagesIfNeeded();
      const packs = this.stateService.selectSnapshot(state => state.tvShopEntertainmentPacks);
      this.carouselService.createCarouselImages(packs);
    }, (error) => {
      this.stateService.handleError(ErrorMessageConstants.ERROR_LOGIN_OR_FETCHING_PACKS, error);
    });
  }

  private navigateToDeeplinkingPagesIfNeeded() {
    this.route.queryParams.subscribe((params: Params) => {
      if (params['EdsProductIds'] || params['salesoffer']) {
        this.stateService.setLoading(false);
        this.router.navigate(['/salesoffer'], {queryParamsHandling: 'merge'});
      }
    });
  }

  private loadMaintenanceModeProperties(config) {
    this.maintenanceModeConfig = config;
    this.isMaintenanceModeActive = config.isActive();
  }

  private checkForPromoStartDate(): void {
    setTimeout(() => {
      const promoDate = this.route.snapshot.queryParams['date'];
      if (promoDate) {
        OmapiIdsUtils.salesOfferDate = promoDate;
      }
    });
  }
}
